import * as React from 'react'
import { AlertCircle as WarningIcon } from 'lucide-react'
import { Link } from 'react-router-dom'
import {
  collectAdditionalProperties,
  useGetPlantNetworkNodeDetailQuery,
  usePlantSessionPaint,
  type PlantAssetDetail,
  type PlantNetworkDeviceDetail,
  type PlantNetworkNodeKind,
  type PlantNetworkParentRef,
  type PlantNetworkProjectRef,
  type PlantNetworkSegmentDetail,
  type PlantNetworkSiteRef
} from '@axilon-apps/domains-ontology-ui-state'
import { Badge } from '../../components/ui/badge'
import { Empty } from '../../components/primitives/Empty'
import { plantHref } from '../../lib/plant-links'
import { EntityDetailsInspector } from './entity/EntityDetailsInspector'
import { PlantStrayPill } from './entity/PlantStrayPill'
import { PlantHref } from './PlantHref'
import { networkIconFor } from './network-icon'
import {
  SessionGhostTile,
  SessionMarkAffordances,
  SessionTitleMark,
  sessionDetailHeaderClass,
  sessionMarkClass,
  useVisibleGhosts,
  type PlantSessionGhostView
} from './session-mark-ui'

export function NetworkDetailPanel({ nodeId }: { nodeId: string }): React.ReactElement {
  const detail = useGetPlantNetworkNodeDetailQuery(nodeId, { skip: !nodeId })

  if (detail.isError) {
    return (
      <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
        <Empty
          title="Couldn't load this network node"
          hint="The node may have been deleted, or the proxy is unreachable."
          icon={WarningIcon}
        />
      </div>
    )
  }
  if (detail.isLoading || !detail.data) {
    return (
      <div className="rounded-xl border bg-card p-6 text-card-foreground shadow-sm">
        <div className="text-sm text-ink-3">Loading network node…</div>
      </div>
    )
  }

  switch (detail.data.kind) {
    case 'Asset':
      return <AssetDetail detail={detail.data} />
    case 'NetworkSegment':
      return <SegmentDetail detail={detail.data} />
    case 'NetworkDevice':
      return <DeviceDetail detail={detail.data} />
  }
}

function decodedKind(entity: { kind?: string }): string | undefined {
  const kind = entity.kind?.trim()
  return kind || undefined
}

function deviceSubtitle(node: {
  kind?: string
  device_kind?: string
  ip_address?: string
}): string {
  return [decodedKind(node) ?? node.device_kind, node.ip_address].filter(Boolean).join(' · ')
}

function AssetDetail({ detail }: { detail: PlantAssetDetail }): React.ReactElement {
  const { asset, parent, siteParent, project, childAssets, segments, devices, computes } = detail
  const assetGhosts = useVisibleGhosts(asset.id, 'Asset')
  const segmentGhosts = useVisibleGhosts(asset.id, 'NetworkSegment')
  const deviceGhosts = useVisibleGhosts(asset.id, 'NetworkDevice')
  const additionalProperties = React.useMemo(
    () => collectAdditionalProperties('Asset', asset as unknown as Record<string, unknown>),
    [asset]
  )
  return (
    <>
      <DetailHeader
        kind="Asset"
        entityId={asset.id}
        isStray={detail.isStray}
        name={asset.display_name ?? asset.name}
        parent={parent}
        siteParent={siteParent}
        project={project}
        kindLabel={decodedKind(asset)}
        subtitle={asset.full_item_name}
      />
      {asset.description && <p className="m-0 max-w-3xl text-sm text-ink-3">{asset.description}</p>}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Sub-assets" value={childAssets.length} />
        <Stat label="Segments" value={segments.length} />
        <Stat label="Devices" value={devices.length} />
        <Stat label="Compute" value={computes.length} />
      </div>
      <ComputeSection computes={computes} />
      <NodeListSection
        label="Sub-assets"
        kind="Asset"
        items={childAssets}
        ghosts={assetGhosts}
        href={(node) => plantHref(node.id, 'Asset', node)}
        title={(node) => node.display_name ?? node.name}
        subtitle={(node) => node.full_item_name}
      />
      <NodeListSection
        label="Network segments"
        kind="NetworkSegment"
        items={segments}
        ghosts={segmentGhosts}
        href={(node) => plantHref(node.id, 'NetworkSegment', node)}
        title={(node) => node.display_name ?? node.name}
        subtitle={(node) => decodedKind(node) ?? node.segment_kind}
      />
      <NodeListSection
        label="Network devices"
        kind="NetworkDevice"
        items={devices}
        ghosts={deviceGhosts}
        href={(node) => plantHref(node.id, 'NetworkDevice', node)}
        title={(node) => node.display_name ?? node.name}
        subtitle={(node) => deviceSubtitle(node)}
      />
      <EntityDetailsInspector entity={asset} additionalProperties={additionalProperties} />
    </>
  )
}

function SegmentDetail({ detail }: { detail: PlantNetworkSegmentDetail }): React.ReactElement {
  const { segment, parent, siteParent, project, devices, computes } = detail
  const deviceGhosts = useVisibleGhosts(segment.id, 'NetworkDevice')
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('NetworkSegment', segment as unknown as Record<string, unknown>),
    [segment]
  )
  return (
    <>
      <DetailHeader
        kind="NetworkSegment"
        entityId={segment.id}
        isStray={detail.isStray}
        name={segment.display_name ?? segment.name}
        parent={parent}
        siteParent={siteParent}
        project={project}
        kindLabel={decodedKind(segment)}
        subtitle={segment.segment_kind}
      />
      {segment.description && (
        <p className="m-0 max-w-3xl text-sm text-ink-3">{segment.description}</p>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Devices" value={devices.length} />
        <Stat label="Compute" value={computes.length} />
        <Stat label="Kind" value={decodedKind(segment) ?? segment.segment_kind ?? '—'} />
      </div>
      <ComputeSection computes={computes} />
      <NodeListSection
        label="Network devices"
        kind="NetworkDevice"
        items={devices}
        ghosts={deviceGhosts}
        href={(node) => plantHref(node.id, 'NetworkDevice', node)}
        title={(node) => node.display_name ?? node.name}
        subtitle={(node) => deviceSubtitle(node)}
      />
      <EntityDetailsInspector entity={segment} additionalProperties={additionalProperties} />
    </>
  )
}

function DeviceDetail({ detail }: { detail: PlantNetworkDeviceDetail }): React.ReactElement {
  const { device, parent, siteParent, project, devices, computes } = detail
  const deviceGhosts = useVisibleGhosts(device.id, 'NetworkDevice')
  const additionalProperties = React.useMemo(
    () =>
      collectAdditionalProperties('NetworkDevice', device as unknown as Record<string, unknown>),
    [device]
  )
  return (
    <>
      <DetailHeader
        kind="NetworkDevice"
        entityId={device.id}
        isStray={detail.isStray}
        name={device.display_name ?? device.name}
        parent={parent}
        siteParent={siteParent}
        project={project}
        kindLabel={decodedKind(device)}
        subtitle={[device.device_kind, device.ip_address].filter(Boolean).join(' · ') || undefined}
      />
      {device.description && (
        <p className="m-0 max-w-3xl text-sm text-ink-3">{device.description}</p>
      )}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Kind" value={decodedKind(device) ?? device.device_kind ?? '—'} />
        <Stat label="IP address" value={device.ip_address ?? '—'} />
        <Stat label="Child devices" value={devices.length} />
      </div>
      <ComputeSection computes={computes} />
      <NodeListSection
        label="Child devices"
        kind="NetworkDevice"
        items={devices}
        ghosts={deviceGhosts}
        href={(node) => plantHref(node.id, 'NetworkDevice', node)}
        title={(node) => node.display_name ?? node.name}
        subtitle={(node) => deviceSubtitle(node)}
      />
      <EntityDetailsInspector entity={device} additionalProperties={additionalProperties} />
    </>
  )
}

function ComputeSection({
  computes
}: {
  computes: PlantNetworkDeviceDetail['computes']
}): React.ReactElement | null {
  if (computes.length === 0) return null
  return (
    <section>
      <SectionHeader label="Compute" subtitle={`${computes.length}`} />
      <div className="flex flex-wrap gap-2">
        {computes.map((compute) => (
          <PlantHref
            key={compute.id}
            id={compute.id}
            label="Compute"
            node={compute}
            className="rounded-md border bg-card px-2 py-1 text-sm font-medium text-ink no-underline shadow-sm hover:border-brand/60"
          >
            {compute.name}
          </PlantHref>
        ))}
      </div>
    </section>
  )
}

function DetailHeader({
  kind,
  entityId,
  isStray,
  name,
  parent,
  siteParent,
  project,
  kindLabel,
  subtitle
}: {
  kind: PlantNetworkNodeKind
  entityId: string
  isStray: boolean
  name: string
  parent?: PlantNetworkParentRef | null
  siteParent?: PlantNetworkSiteRef | null
  project?: PlantNetworkProjectRef | null
  kindLabel?: string
  subtitle?: string
}): React.ReactElement {
  const Icon = networkIconFor(kind)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(entityId)
  return (
    <header className={sessionDetailHeaderClass('flex flex-col gap-2', mark)}>
      <div className="flex flex-wrap items-center gap-2 text-xs text-ink-3">
        <span className="text-11 font-semibold uppercase tracking-[0.04em]">{kind}</span>
        {parent && (
          <>
            <span aria-hidden>·</span>
            <ParentLink href={plantHref(parent.id, parent.kind, parent)} name={parent.name} />
          </>
        )}
        {!parent && siteParent && (
          <>
            <span aria-hidden>·</span>
            <ParentLink
              href={plantHref(siteParent.id, 'Site', siteParent)}
              name={siteParent.name}
            />
          </>
        )}
        {project && (
          <>
            <span aria-hidden>·</span>
            <span>{project.name}</span>
          </>
        )}
      </div>
      <div className="flex items-center gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-md bg-brand-soft text-brand">
          <Icon size={18} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="m-0 truncate text-2xl font-semibold text-ink">{name}</h1>
            <Badge variant="outline">{kind}</Badge>
            {kindLabel && <Badge variant="secondary">{kindLabel}</Badge>}
            <SessionTitleMark mark={mark} />
            <PlantStrayPill isStray={isStray} />
          </div>
          {subtitle && <p className="m-0 mt-0.5 text-sm text-ink-3">{subtitle}</p>}
        </div>
      </div>
    </header>
  )
}

function ParentLink({ href, name }: { href: string; name: string }): React.ReactElement {
  return (
    <Link
      to={href}
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-medium text-ink-2 no-underline hover:bg-accent hover:text-accent-foreground"
    >
      <span aria-hidden>↑</span>
      <span>{name}</span>
    </Link>
  )
}

function Stat({ label, value }: { label: string; value: number | string }): React.ReactElement {
  return (
    <div className="rounded-xl border bg-card p-3 text-card-foreground shadow-sm">
      <div className="text-11 font-semibold uppercase tracking-[0.04em] text-ink-3">{label}</div>
      <div className="mt-1 text-lg font-semibold text-ink">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
    </div>
  )
}

function NodeTile({
  entityId,
  href,
  kind,
  title,
  subtitle
}: {
  entityId: string
  href: string
  kind: PlantNetworkNodeKind
  title: string
  subtitle?: string
}): React.ReactElement {
  const Icon = networkIconFor(kind)
  const { markFor } = usePlantSessionPaint()
  const mark = markFor(entityId)
  return (
    <Link
      to={href}
      className={sessionMarkClass(
        'flex items-start gap-2 rounded-xl border bg-card p-3 text-card-foreground no-underline shadow-sm hover:border-brand/60',
        mark
      )}
    >
      <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-md bg-brand-soft text-brand">
        <Icon size={15} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 items-center gap-1.5">
          <span className="block min-w-0 flex-1 truncate text-sm font-medium text-ink">
            {title}
          </span>
          <SessionMarkAffordances mark={mark} />
        </span>
        {subtitle && <span className="block truncate text-xs text-ink-3">{subtitle}</span>}
      </span>
    </Link>
  )
}

function NetworkGhostTile({
  kind,
  ghost
}: {
  kind: PlantNetworkNodeKind
  ghost: PlantSessionGhostView
}): React.ReactElement {
  const Icon = networkIconFor(kind)
  return (
    <SessionGhostTile
      name={ghost.name ?? ghost.id}
      view={ghost.view}
      icon={
        <span className="mt-0.5 grid size-7 shrink-0 place-items-center rounded-md bg-st-down-bg text-st-down">
          <Icon size={15} />
        </span>
      }
    />
  )
}

function NodeListSection<T extends { id: string }>({
  label,
  kind,
  items,
  ghosts = [],
  href,
  title,
  subtitle
}: {
  label: string
  kind: PlantNetworkNodeKind
  items: T[]
  ghosts?: PlantSessionGhostView[]
  href: (item: T) => string
  title: (item: T) => string
  subtitle?: (item: T) => string | undefined
}): React.ReactElement | null {
  const count = items.length + ghosts.length
  if (count === 0) return null
  return (
    <section>
      <SectionHeader label={label} subtitle={`${count}`} />
      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {items.map((item) => (
          <NodeTile
            key={item.id}
            entityId={item.id}
            href={href(item)}
            kind={kind}
            title={title(item)}
            subtitle={subtitle?.(item)}
          />
        ))}
        {ghosts.map((ghost) => (
          <NetworkGhostTile key={ghost.id} kind={kind} ghost={ghost} />
        ))}
      </div>
    </section>
  )
}

function SectionHeader({
  label,
  subtitle
}: {
  label: string
  subtitle?: string
}): React.ReactElement {
  return (
    <div className="mb-2 flex items-center justify-between gap-2">
      <h2 className="m-0 text-sm font-semibold text-ink">{label}</h2>
      {subtitle && <span className="text-xs text-ink-3">{subtitle}</span>}
    </div>
  )
}
